import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Button, Checkbox, CircularProgress, Collapse, FormControlLabel, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { PageData } from "../../../models";
import { actionCreators, ApplicationState, FetchState, selectors } from "../../../store";
import palette from '../../../theme/palette';
import { formatDateTime } from "../../../utils";
import Page from "../../common/Page";
import FetchHelper from "../../FetchHelper";
import AeGrid from "./AeGrid";
import { useDispatch } from "react-redux";

// -------------
// FETCH ID
const fetchId = "AE_LIST";
const formId = "AE_SEARCH_FORM";

// 検索フォーム
export interface SearchItemForm {
    limit: number;
    page: number;
    occurrence_time_from: Date | null;
    occurrence_time_to: Date | null;
    received: string;
}

// 初期値設定
const initialValue: SearchItemForm = {
    limit: 50,
    page: 0,
    occurrence_time_from: null,
    occurrence_time_to: null,
    received: "0"
}

const initialPageData: PageData = {
    rowFrom: '0',
    rowTo: '0',
    totalRow: '0',
    currPage: '0',
    totalPage: '0',
};

// -------------
// Props
export type AeListProps = ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps;

// -------------
// Component
export const AeList = (props: AeListProps) => {
    const {
        onSearch,
        onError,
        initialPageSize
    } = props;

    const { handleSubmit, control, setValue, getValues } = useForm<SearchItemForm>({
        defaultValues: {...initialValue, limit: initialPageSize["telemetry"]}
    });
    const [openSearch, setOpenSearch] = React.useState(false);

    localStorage.setItem('lastActiveFormPath', window.location.pathname + window.location.search);
    localStorage.setItem('title', '死活監視');
    const [pageData, setPageData] = React.useState(initialPageData);
    const [listData, setListData] = React.useState<any>([]);
    const [colState, setColState] = useState<any>();
    const [searchParams] = useSearchParams();
    const [latestUpdates, setLatestUpdates] = React.useState("");
    const [keyword, setKeyword] = useState("");
    const [loadingFlag, setLoadingFlag] = React.useState(true);
    const [checked, setChecked] = React.useState(true);
    const [checkboxFlag, setCheckboxFlag] = React.useState(true);

    const myParam = searchParams.get('id');
    const gridRef = useRef() as any;
    const dispatch = useDispatch();
    // reload data when change URL query strings
    const watchChangeUrl = searchParams.get('customer_id');

    React.useEffect(() => {
        setTimeout(() => {
            localStorage.removeItem("currentUrl");
        },2000)
    },[])

    React.useEffect(() => {
        if (!loadingFlag){
            const params = new Map<string, any>();

            params.set('code', 'AE');
            params.set('limit', getValues('limit'));
            params.set('path', searchParams.get('path') ?? 'root');
            if(searchParams.get('customer_id')) params.set('customer_id', searchParams.get('customer_id'));

            handleClearSeachCondition();
            setLoadingFlag(true);
            onSearch(params);
        }        
    }, [watchChangeUrl]);

    // 検索実行
    const handleSearchSubmit = (value: SearchItemForm) => {
        let isError = false;

        if (gridRef && gridRef.current) {
            setColState(gridRef.current.saveState());
        }

        const params = new Map<string, any>();
        params.set("code", "AE")
        if (value.limit) params.set("limit", value.limit);
        if (value.page) params.set("page", value.page - 1);
        if (value.occurrence_time_from) params.set("occurrence_time_from", formatDateTime(value.occurrence_time_from));
        if (value.occurrence_time_to) params.set("occurrence_time_to", formatDateTime(value.occurrence_time_to));
        params.set("received", value.received);

        if (value.occurrence_time_from || value.occurrence_time_to) {
            params.set("mainid_aggregationed", checked ? 1 : 0)
        }

        params.set('path', searchParams.get('path') ?? 'root');
        if(searchParams.get('customer_id')) params.set('customer_id', searchParams.get('customer_id'));
        
        if (!isError) {
            setLoadingFlag(true);
            onSearch(params);
        }
    }

    // 初回検索実行
    React.useEffect(() => {
        handleSearchSubmit(getValues());
        // eslint-disable-next-line
    }, []);

    // 検索正常終了時
    const handleSuccess = (data: any) => {
        if (data.results) {
            if (gridRef && gridRef.current) {
                gridRef.current.setRowData(data.results);
            }

            setListData(() => [...data.results]);

            // if (myParam) {
            //     listData.forEach((item: any) => {
            //         item.serial_no = myParam
            //     });
            //     setListData(()=>[...listData])
            // } 
            // else {
            //     listData.forEach((item: any) => {
            //         item.serial_no = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
            //     });
            //     setListData(()=>[...listData])
            // }

            const totalCount = data.total_count;
            const pageSize = getValues('limit');
            const page = Number(data.page) + 1;
            const totalPage = Math.ceil(totalCount / pageSize);
            const rowFrom = (pageSize * page - pageSize) + 1;
            const rowTo = (pageSize * page) > totalCount ? totalCount : (pageSize * page);

            const updateData: PageData = {
                rowFrom: rowFrom.toLocaleString(),
                rowTo: rowTo.toLocaleString(),
                totalRow: totalCount.toLocaleString(),
                currPage: page.toLocaleString(),
                totalPage: totalPage.toLocaleString(),
            };

            setPageData(updateData);
        } else {
            setListData(() => [...[] as any]);
        }
        setLatestUpdates(formatDateTime(new Date()));
        setLoadingFlag(false);
    };

    // 検索エラー時
    const handleError = (success: boolean, data: any, error: any) => {
        onError(success, data, error);
        setLoadingFlag(false);
        gridRef.current.hideOverlay();
    };

    // ページ数変更時にAPI検索する
    const handleChangePageData = (name: any, value: any) => {
        // 値変更
        setValue(name, value);
        if (name === 'limit') {
            const initialPageSizeTemp = { ...initialPageSize, telemetry: Number(value) };
            dispatch({type:"SET_INIT_PAGE_SIZE",payload:{data:initialPageSizeTemp}});
            setValue("page", 0);
        }
        handleSearchSubmit(getValues());
    };

    const onRefreshGrid = () => {
        handleSearchSubmit(getValues());
    }

    React.useEffect(() => {
        // グラフ表示用データの更新
        // --------------------------------
        if (myParam) {
            listData.forEach((item: any) => {
                item.send_time = myParam
            });
            setListData(() => [...listData])
        }
        else {
            listData.forEach((item: any) => {
                item.send_time = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
            });
            setListData(() => [...listData])
        }
        // eslint-disable-next-line
    }, [myParam]);

    const handleChangeKeyword = (value: string) => {
        setKeyword(value);
        if (gridRef && gridRef.current) {
            setColState(gridRef.current.saveState());
        }
    }

    // ハンドルオープン
    const handleOpen = (setState: any) => {
        if (gridRef && gridRef.current) {
            setColState(gridRef.current.saveState());
        }
        setState((prev: boolean) => !prev);
    };

    // 検索条件をクリアします
    const handleClearSeachCondition = () => {
        setValue("occurrence_time_from", null)
        setValue("occurrence_time_to", null)
        setValue("received", "0");
        setCheckboxFlag(true);
        setChecked(true);
    };

    const setValueToggleButton = (s: any, value: string) => {
        if (value !== null) {
            setValue(s, value)
            if (value === "0") {
                setCheckboxFlag(true);
                setChecked(true);
            }
            else {
                setCheckboxFlag(false);
            }
        }
    };

    const handleSearch = (data: any) => {
        setValue("page", 0)
        handleSearchSubmit(getValues())
    };

    return (
        <>
            {loadingFlag && (
                <div style={{ top: "0px", left: '0px', position: "fixed", zIndex: 1200, width: '100%', height: '100%', padding: '50vh 50% 50% 50%', background: '#00000030' }}>
                    <CircularProgress />
                </div>
            )}
            <div style={{ height: '100%', width: '100%' }}>
                <form onSubmit={handleSubmit(handleSearchSubmit)}>
                    <Stack spacing={1} mb="10px" style={{ border: openSearch ? "2px solid gray" : '', borderRadius: "15px" }}>
                        <div style={{ cursor: 'pointer', display: 'inline-block', marginLeft: '10px' }} onClick={() => { handleOpen(setOpenSearch); }}>
                            <div style={{ display: 'inline-block' }}>
                                <Typography variant="h6" >検索条件</Typography>
                            </div>
                            <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                {
                                    openSearch ? <ArrowDropDownIcon /> : <ArrowRightIcon />
                                }
                            </div>
                        </div>
                        <div style={{ marginLeft: "10px", marginRight: "10px", marginBottom: "10px" }}>
                            <Collapse in={openSearch} timeout="auto" unmountOnExit>
                                <Grid
                                    container
                                    justifyContent="flex-start"
                                    alignItems={"end"}
                                    spacing={1}
                                    direction="row"
                                >
                                    <Grid item minWidth="29.5rem">
                                        <Stack spacing={1} direction="row">
                                            <Controller
                                                control={control}
                                                name="occurrence_time_from"
                                                render={({ field: { value } }) => (
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DateTimePicker
                                                            inputFormat="YYYY/MM/DD HH:mm:ss"
                                                            renderInput={(props: any) => (
                                                                <TextField
                                                                    size="small"
                                                                    {...props}
                                                                    style={{ background: palette.background.paper }}
                                                                />
                                                            )}
                                                            label="受信日時From"
                                                            value={value}
                                                            onChange={(newValue: any) => {
                                                                setValue("occurrence_time_from", newValue ? newValue.$d : null);
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                )}
                                            />
                                            <Stack justifyContent="center" alignItems="center">
                                                <div>～</div>
                                            </Stack>
                                            <Controller
                                                control={control}
                                                name="occurrence_time_to"
                                                render={({ field: { value } }) => (
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DateTimePicker
                                                            inputFormat="YYYY/MM/DD HH:mm:ss"
                                                            renderInput={(props: any) => (
                                                                <TextField
                                                                    size="small"
                                                                    {...props}
                                                                    style={{ background: palette.background.paper }}
                                                                />
                                                            )}
                                                            label="受信日時To"
                                                            value={value}
                                                            onChange={(newValue: any) => {
                                                                setValue("occurrence_time_to", newValue ? newValue.$d : null);
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                )}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item>
                                        <Stack spacing={4} direction={{ xs: 'column', sm: 'row' }} style={{ marginLeft: '10px' }}>
                                            <Controller
                                                control={control}
                                                name="received"
                                                render={({ field: { value } }) => (
                                                    <Grid container spacing={1} direction="row" >
                                                        <ToggleButtonGroup
                                                            color="primary"
                                                            value={value}
                                                            exclusive
                                                            onChange={(e, value) => setValueToggleButton('received', value)}
                                                            aria-label="Platform"
                                                            size="small"
                                                            aria-labelledby="システム管理者フラグ"
                                                        >
                                                            <ToggleButton value="1">受信している</ToggleButton>
                                                            <ToggleButton value="0">受信していない</ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Grid>
                                                )}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item style={{ marginLeft: '15px' }}>
                                        <FormControlLabel
                                            disabled={checkboxFlag}
                                            control={
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => setChecked(!checked)} />}
                                            label="装置IDで集約"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Button variant="outlined" style={{ background: palette.background.paper }} onClick={handleClearSeachCondition}>クリア</Button>
                                    </Grid>
                                    <Grid item >
                                        <Button variant="contained" onClick={handleSearch}>
                                            検索
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </div>
                    </Stack>
                </form>
                <div style={{ marginBottom: "10px" }}>
                    <Stack direction="row">
                        <Typography variant="body2" style={{ textAlign: "center", lineHeight: "40px" }}>
                            最終表示日時&emsp;{latestUpdates}
                        </Typography>
                        <div style={{ marginInlineStart: 'auto' }}>
                            <Grid container justifyContent="flex-start" alignItems={"center"} direction="row" spacing={1} >
                                <Grid item>
                                    <Page onRefreshGrid={onRefreshGrid}></Page>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        autoComplete="searchStr"
                                        name="keyword"
                                        type="text"
                                        label="フィルター"
                                        size="small"
                                        value={keyword}
                                        onChange={e => handleChangeKeyword(e.target.value)}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" onClick={e => handleChangeKeyword("")}>クリア</Button>
                                </Grid>
                            </Grid>
                        </div>
                    </Stack>
                </div>
                <div>
                    <AeGrid
                        ref={gridRef}
                        colState={colState}
                        pageSize={getValues('limit')}
                        pageData={pageData}
                        onChangeSearchCondition={handleChangePageData}
                        keyword={keyword}
                        openSearch={openSearch}
                    />
                </div>
            </div>
            <FetchHelper fetchId={fetchId} onComplete={(success, data, error) =>
                success ? handleSuccess(data) : handleError(success, data, error)} />
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    fetchState: selectors.getFetchState(
        state,
        fetchId
    ) as FetchState,
    formState: selectors.getFormState(state, formId),
    initialPageSize: state.app.initialPageSize,
});

const mapDispatchToProps = {
    onSearch: (params: { [key: string]: any }) => actionCreators.fetch(fetchId, `/telemetry/get`, "GET", params),
    onError: (success: boolean, data: any, error: any) =>
        actionCreators.showMessage({
            type: "error",
            title: "死活監視検索",
            body: "死活監視を検索できません。" + error,
        }),
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AeList as any);
